import { LIVE_BANNER_APPEARANCE_STORAGE_KEY } from '../constants/liveBanner';

const LIVE_BANNER_TRACKER_MAX_ENTRIES = 5;

export type LiveBannerTrackerEntry = [bannerKey: string, appearanceCount: number];

/** Per-user banner appearance counts (key = Firebase uid). */
export type LiveBannerTrackerState = Record<string, LiveBannerTrackerEntry[]>;

function isTrackerEntry(value: unknown): value is LiveBannerTrackerEntry {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'string' &&
    typeof value[1] === 'number' &&
    value[1] >= 0
  );
}

function isUserTrackerEntries(value: unknown): value is LiveBannerTrackerEntry[] {
  return Array.isArray(value) && value.every(isTrackerEntry);
}

function isTrackerState(value: unknown): value is LiveBannerTrackerState {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return Object.values(value).every(isUserTrackerEntries);
}

function parseLiveBannerTrackerState(raw: string | null): LiveBannerTrackerState {
  if (!raw) {
    return {};
  }
  try {
    const data = JSON.parse(raw) as unknown;
    if (isTrackerState(data)) {
      return data;
    }
  } catch {
    // ignore corrupt storage
  }
  return {};
}

function getEntriesForUser(state: LiveBannerTrackerState, userId: string): LiveBannerTrackerEntry[] {
  return state[userId] ?? [];
}

function persistLiveBannerTrackerState(state: LiveBannerTrackerState) {
  localStorage.setItem(LIVE_BANNER_APPEARANCE_STORAGE_KEY, JSON.stringify(state));
}

function persistUserEntries(state: LiveBannerTrackerState, userId: string, entries: LiveBannerTrackerEntry[]) {
  persistLiveBannerTrackerState({ ...state, [userId]: entries });
}

/**
 * Returns whether the banner may be shown and records an appearance for this user.
 * Stored count starts at 0; each allowed show increments while count < appearanceLimit.
 * When `userId` is missing (guest), appearance limits are not applied.
 */
export function checkAppearanceLimitLeft(
  userId: string | undefined,
  bannerKey: string,
  appearanceLimit: number,
): boolean {
  if (!userId) {
    return true;
  }

  const state = parseLiveBannerTrackerState(localStorage.getItem(LIVE_BANNER_APPEARANCE_STORAGE_KEY));
  const entries = [...getEntriesForUser(state, userId)];
  const entry = entries.find(([key]) => key === bannerKey);

  if (entry) {
    if (entry[1] >= appearanceLimit) {
      return false;
    }
    entry[1]++;
    persistUserEntries(state, userId, entries);
    return true;
  }

  const next: LiveBannerTrackerEntry[] = [[bannerKey, 1], ...entries];
  if (next.length > LIVE_BANNER_TRACKER_MAX_ENTRIES) {
    next.pop();
  }
  persistUserEntries(state, userId, next);
  return true;
}
